import React, { useRef, useState } from "react";
import mammoth from "mammoth/mammoth.browser";
import { ToastMsg } from "../pages/ToastMsg";

/**
 * Upload a Word (.docx) file and convert it to clean HTML in the browser
 * (via mammoth.js), then hand the HTML back through `onHtml`. The caller
 * typically passes its editor's setter, e.g. <DocxUploadButton onHtml={setContentBody} />,
 * so the converted content loads into the existing editor for review before saving.
 */
const DocxUploadButton = ({ onHtml, label = "Upload .docx" }) => {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const handleFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    // Allow re-selecting the same file later.
    event.target.value = "";
    if (!file) return;

    const isDocx =
      file.name.toLowerCase().endsWith(".docx") ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    if (!isDocx) {
      ToastMsg("error", "Please choose a .docx file (legacy .doc isn't supported).");
      return;
    }

    setLoading(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });
      onHtml(result.value || "");
      const warnings = (result.messages || []).filter((m) => m.type === "warning");
      if (warnings.length) {
        ToastMsg(
          "warning",
          `Imported with ${warnings.length} formatting note(s) — please review.`
        );
      } else {
        ToastMsg("success", "Word document imported. Review and Save.");
      }
    } catch (err) {
      console.error("docx conversion failed", err);
      ToastMsg("error", "Couldn't read that Word file. Make sure it's a valid .docx.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-2">
      <input
        ref={inputRef}
        type="file"
        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        style={{ display: "none" }}
        onChange={handleFile}
      />
      <button
        type="button"
        className="btn btn-outline-primary btn-sm"
        disabled={loading}
        onClick={() => inputRef.current && inputRef.current.click()}
      >
        {loading ? "Importing…" : label}
      </button>
      <small className="text-muted ms-2">
        Imports a Word .docx into the editor below for review.
      </small>
    </div>
  );
};

export default DocxUploadButton;
